import { useCallback, useEffect, useState } from 'react';
import Card from '../../../components/atoms/card';
import MovementService, { IMovement } from '../../../services/Movement.service';
import Input from '../../../components/atoms/input';
import InputEmails from '../../../components/molecules/input-emails';
import SVGIcon from '../../../components/atoms/icons';
import Button from '../../../components/atoms/button';
import InputCalendar from '../../../components/molecules/input-calendar';
import Select from '../../../components/atoms/select';
import { useRouteLoaderData } from 'react-router-dom';
import UserService, { IUser } from '../../../services/User.service';
import ClientService, { IClient } from '../../../services/Client.service';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../utils/types/editable';
import YardService, { IYard } from '../../../services/Yard.service';
import ShipService, { IShip } from '../../../services/Ship.service';
import { IContainer } from '../../../services/Container.service';
import { NotificationToast } from '../../../components/organisms/notification-toast';
import { HomeLoaderData } from '../../../types/homeLoaderData';
import Label from '../../../components/atoms/text/label';

interface MovementForm extends Omit<IMovement, 'owner'> {
	owner: string[];
	lc: boolean;
	cb: boolean;
}

const movementInitialState: MovementForm = {
	containers: [],
	date: new Date(),
	isArrived: true,
	travelCode: '',
	yard: {},
	ship: {},
	owner: [],
	lc: true,
	cb: true,
};

const MovementManual = () => {
	const { t } = useTranslation();
	const { user, accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const [form, setForm] = useState<MovementForm>(movementInitialState);

	const [yards, setYards] = useState<IYard[]>([]);
	const [ships, setShips] = useState<IShip[]>([]);
	const [error, setError] = useState<string>('');

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const fetchYards = useCallback(async () => {
		if (!accountId) return;
		const response = await YardService.getAll({
			isActive: 1,
			idClient: Number(accountId),
		});
		if (!response) return;
		setYards(response.data);
	}, [accountId]);

	useEffect(() => {
		fetchYards();
	}, [fetchYards]);

	const fetchShips = useCallback(async () => {
		if (!accountId) return;
		const response = await ShipService.getAll({
			isActive: 1,
			idClient: Number(accountId),
		});
		if (!response) return;
		setShips(response.data);
	}, [accountId]);

	useEffect(() => {
		fetchShips();
	}, [fetchShips]);

	const addContainer = () => {
		const containers = [...form.containers];
		containers.push({
			temperature: '',
			humidity: '',
			ventilation: '',
			atmosferaControlada: false,
			nivelCo2: '',
			label: '',
			position: '',
			carrier: '',
			owner: '',
		});
		setForm((prev) => ({ ...prev, containers }));
	};

	const removeContainer = (index: number) => {
		const containers = [...form.containers];
		containers.splice(index, 1);
		setForm((prev) => ({ ...prev, containers }));
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	};

	const handleYard = (e: EditableChangeEvent) => {
		const yard = yards.find((item) => item.name === e.target.value);
		if (!yard) return;
		handleInput({ target: { name: 'yard', value: yard } });
	};

	const handleShip = (e: EditableChangeEvent) => {
		const ship = ships.find((item) => item.name === e.target.value);
		if (!ship) return;
		handleInput({ target: { name: 'ship', value: ship } });
	};

	const handleContainer = (
		e: React.ChangeEvent<HTMLInputElement>,
		index: number
	) => {
		const containers = [...form.containers];
		containers[index][e.target.name as keyof IContainer] = e.target.value;
		handleInput({ target: { name: 'containers', value: containers } });
	};

	const handleAtmosferaControlada = (index: number) => {
		const containers = [...form.containers];
		containers[index].atmosferaControlada =
			!containers[index].atmosferaControlada;
		handleInput({ target: { name: 'containers', value: containers } });
	};

	const handleDate = (date: Date) => {
		handleInput({ target: { name: 'date', value: date } });
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const emailInput = e.currentTarget.querySelector<HTMLInputElement>(
			'input[type="email"][name="owner"]'
		);
		if (emailInput && !emailInput.reportValidity()) return;
		if (
			!form.yard ||
			!form.ship ||
			!form.yard.id ||
			!form.date ||
			!form.travelCode
		) {
			setError('alert.error.mandatory');
			return;
		}

		const { lc, cb, ...data } = form;
		const response = await MovementService.create(data, setError);
		if (response) {
			NotificationToast.Success(
				`${t('movement.title')} ${t('alert.success.create')}`
			);
			setForm(movementInitialState);
		}
	};

	return (
		<Card className="flex flex-col flex-grow">
			<form
				className="flex flex-col flex-grow p-4 gap-6"
				autoComplete="off"
				noValidate
				onSubmit={handleSubmit}
			>
				<div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3 items-start">
					<Select
						name="yard"
						value={form.yard.name || ''}
						onChange={handleYard}
						label={t('port_management.yard')}
						placeholder={t('port_management.yard')}
						options={yards.map((item) => item.name)}
						required
					/>
					<Select
						name="ship"
						value={form.ship.name || ''}
						onChange={handleShip}
						label={t('port_management.ship')}
						placeholder={t('port_management.ship')}
						options={ships.map((item) => item.name)}
						required
					/>
					<Input
						name="travelCode"
						value={form.travelCode}
						onChange={handleInput}
						label={t('movement.travel_code')}
						placeholder={t('movement.travel_code')}
						required
					/>
					<InputCalendar
						date={form.date}
						label={t('date.retroactive')}
						onDateChange={handleDate}
						placeholder={t('date.retroactive')}
						required
					/>
					<InputEmails
						name="owner"
						value={form.owner}
						onChange={handleInput}
						label={t('movement.client_email')}
					/>
				</div>
				<div className="flex flex-col gap-y-6">
					{form.containers.map((item, index) => (
						<div
							className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-3 items-center"
							key={index}
						>
							<div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3">
								<Input
									name="label"
									value={item.label}
									onChange={(e) => handleContainer(e, index)}
									label={t('port_management.container')}
									placeholder={t('port_management.container')}
									required
								/>
								<Input
									name="temperature"
									value={item.temperature || ''}
									onChange={(e) => handleContainer(e, index)}
									label={t(
										'port_management.containers.set_point'
									)}
									placeholder={t(
										'port_management.containers.set_point'
									)}
									type="number"
								/>
								<Input
									name="owner"
									value={item.owner}
									onChange={(e) => handleContainer(e, index)}
									label={t(
										'port_management.containers.owner'
									)}
									placeholder={t(
										'port_management.containers.owner'
									)}
								/>
								<Input
									name="position"
									value={item.position}
									onChange={(e) => handleContainer(e, index)}
									label={t(
										'port_management.containers.position'
									)}
									placeholder={t(
										'port_management.containers.position'
									)}
								/>
								<Input
									name="carrier"
									value={item.carrier}
									onChange={(e) => handleContainer(e, index)}
									label={t('port_management.carrier')}
									placeholder={t('port_management.carrier')}
								/>
								<Input
									name="humidity"
									value={item.humidity || ''}
									onChange={(e) => handleContainer(e, index)}
									label={t(
										'port_management.containers.humidity'
									)}
									placeholder={t(
										'port_management.containers.humidity'
									)}
									type="number"
								/>
								<Input
									name="ventilation"
									value={item.ventilation || ''}
									onChange={(e) => handleContainer(e, index)}
									label={t(
										'port_management.containers.ventilation'
									)}
									placeholder={t(
										'port_management.containers.ventilation'
									)}
									type="number"
								/>
								<Input
									name="nivelCo2"
									value={item.nivelCo2 || ''}
									onChange={(e) => handleContainer(e, index)}
									label={t(
										'port_management.containers.nivel_co2'
									)}
									placeholder={t(
										'port_management.containers.nivel_co2'
									)}
									type="number"
								/>
								<Label
									className="flex items-center gap-2"
									htmlFor={`atmosferaControlada-${index}`}
								>
									<input
										id={`atmosferaControlada-${index}`}
										type="checkbox"
										checked={
											!!item.atmosferaControlada
										}
										onChange={() =>
											handleAtmosferaControlada(index)
										}
									/>
									{t(
										'port_management.containers.atmosfera_controlada'
									)}
								</Label>
							</div>
							<SVGIcon
								className="h-5 w-auto cursor-pointer fill-red"
								icon="Delete"
								onClick={() => removeContainer(index)}
							/>
						</div>
					))}
					<Button className="w-fit" onClick={() => addContainer()}>
						{t('movement.add_container')}
					</Button>
				</div>
				<Button
					className="place-self-end"
					type="submit"
					disabled={form.containers.length == 0}
				>
					{t('misc.save')}
				</Button>
			</form>
		</Card>
	);
};

export default MovementManual;
